import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Info, Sparkles, UserCheck, QrCode, Expand } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

const SITE_URL = "https://gpsofertas.com";
const REGISTER_URL = `${SITE_URL}/registro`;
const SHORT_REGISTER = "gpsofertas.com/registro";
const SHORT_SITE = "gpsofertas.com";

export default function PromoterPoster({ promoter }) {
  const name = promoter?.name || "tu promotor";
  const promoterId = promoter?.id;
  const affiliateUrlWithPid = promoterId ? `${REGISTER_URL}?pid=${encodeURIComponent(promoterId)}` : REGISTER_URL;

  const [modalPoster, setModalPoster] = useState(null);
  const [printTarget, setPrintTarget] = useState(null);

  const printPoster = (which) => {
    setPrintTarget(which);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintTarget(null), 500);
    }, 120);
  };

  const promoterPosterProps = {
    overline: "DIRECTORIO LOCAL · OFERTAS",
    vecino: "¡COMERCIANTE!",
    headline: (
      <>
        Llega a más<br />
        <span className="poster-grad">clientes</span>
        <br />
        en tu colonia
      </>
    ),
    sub: "Publica tus ofertas · Gana lealtad · Rifas mensuales · Directorio digital para negocios locales",
    scan: "ESCANEA Y REGÍSTRATE",
    qrUrl: affiliateUrlWithPid,
    qrUrlLabel: SHORT_REGISTER,
    footerLeftLabel: "Tu promotor",
    footerLeftName: name,
    footerLeftHint: (
      <>
        <UserCheck className="inline w-3 h-3 mr-1" />
        Menciona su nombre al registrarte
      </>
    ),
    footerRight: (
      <>
        <Sparkles className="poster-sparkle" />
        <div className="poster-footer-msg">
          <strong>$199 MXN/mes.</strong> Sin contratos. Cancela cuando quieras.
        </div>
      </>
    ),
  };

  const customerPosterProps = {
    overline: "DIRECTORIO LOCAL · OFERTAS",
    vecino: "¡VECINO!",
    headline: (
      <>
        Descubre las<br />
        <span className="poster-grad">ofertas</span>
        <br />
        de tu colonia
      </>
    ),
    sub: "Ahorra en lo que ya compras · Acumula puntos · Canjea premios · Gratis",
    scan: "ESCANEA Y DESCUBRE",
    qrUrl: SITE_URL,
    qrUrlLabel: SHORT_SITE,
    footerLeftLabel: "Para clientes",
    footerLeftName: "Sin costo",
    footerLeftHint: (
      <>
        <UserCheck className="inline w-3 h-3 mr-1" />
        Regístrate y empieza a acumular puntos
      </>
    ),
    footerRight: (
      <>
        <Sparkles className="poster-sparkle" />
        <div className="poster-footer-msg">
          <strong>Ofertas reales</strong> de comercios cerca de ti. Rifas mensuales.
        </div>
      </>
    ),
  };

  return (
    <div className="space-y-6">
      {/* CARTEL DE PROMOTOR — para afiliar comercios */}
      <PosterCard
        iconBg="bg-[#F5F3FF]"
        iconColor="text-[#7C3AED]"
        icon={<Printer className="w-5 h-5" />}
        title="Código Qr para que los comercios se afilien con tu nombre de promotor en automático"
        description={
          <>
            Imprime este cartel y entrégalo a los comercios que quieras afiliar. Cuando lo escaneen llegan directo a la página de registro y <strong>tu nombre queda como referidor</strong> al registrarse.
          </>
        }
        tip={
          <>
            Deja un cartel en la entrada de cada negocio que visites. Tamaño <strong>carta (8.5" × 11")</strong>. Cuantos más negocios se afilien con tu nombre, más comisiones generas.
          </>
        }
        printLabel="Imprimir / Guardar PDF"
        onPrint={() => printPoster("promoter")}
        printDataTestId="promoter-poster-print-btn"
        onPreviewClick={() => setModalPoster("promoter")}
        previewDataTestId="promoter-poster-preview-btn"
        testId="promoter-poster"
        posterProps={promoterPosterProps}
      />

      {/* QR PARA CLIENTES — para que vecinos descubran ofertas */}
      <PosterCard
        iconBg="bg-[#FEF3F2]"
        iconColor="text-[#E05A47]"
        icon={<QrCode className="w-5 h-5" />}
        title="Código Qr para pegar en paredes y postes promocionando el directorio"
        description={
          <>
            Comparte este cartel con clientes y vecinos para que descubran las ofertas de su colonia en <strong>gpsofertas.com</strong>.
          </>
        }
        tip={
          <>
            Pégalo en tu local, plazas o puntos de reunión. Tamaño <strong>carta (8.5" × 11")</strong>. Cuantos más clientes entren, más valor reciben los comercios que afiliaste.
          </>
        }
        printLabel="Imprimir / Guardar PDF"
        onPrint={() => printPoster("customers")}
        printDataTestId="customer-qr-print-btn"
        onPreviewClick={() => setModalPoster("customers")}
        previewDataTestId="customer-qr-preview-btn"
        testId="customer-poster"
        posterProps={customerPosterProps}
      />

      {/* Hidden print containers */}
      <div className="hidden print:block">
        {printTarget === "promoter" && (
          <div className="poster-wrapper" data-testid="promoter-poster-print-wrapper">
            <Poster {...promoterPosterProps} data-testid="promoter-poster-print" />
          </div>
        )}
        {printTarget === "customers" && (
          <div className="poster-wrapper" data-testid="customer-poster-print-wrapper">
            <Poster {...customerPosterProps} data-testid="customer-poster-print" />
          </div>
        )}
      </div>

      {/* Preview modal */}
      <Dialog open={!!modalPoster} onOpenChange={(o) => !o && setModalPoster(null)}>
        <DialogContent className="max-w-4xl w-full p-3 sm:p-4" data-testid="poster-preview-modal">
          <DialogTitle className="sr-only">
            {modalPoster === "promoter" ? "Cartel de promotor" : "Cartel para clientes"}
          </DialogTitle>
          <div className="w-full max-h-[85vh] overflow-auto">
            {modalPoster === "promoter" && (
              <ScaledPoster {...promoterPosterProps} testId="promoter-poster-modal" />
            )}
            {modalPoster === "customers" && (
              <ScaledPoster {...customerPosterProps} testId="customer-poster-modal" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Reusable card: left = info + print button, right = clickable preview
 * ------------------------------------------------------------------------- */
function PosterCard({
  iconBg,
  iconColor,
  icon,
  title,
  description,
  tip,
  printLabel,
  onPrint,
  printDataTestId,
  onPreviewClick,
  previewDataTestId,
  testId,
  posterProps,
}) {
  return (
    <div className="bg-white border border-border rounded-2xl p-4 sm:p-6">
      <div className="grid md:grid-cols-[1fr_auto] gap-5 sm:gap-6 items-start">
        {/* Left: info + buttons */}
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${iconBg} grid place-items-center shrink-0`}>
            <span className={iconColor}>{icon}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg sm:text-xl md:text-2xl tracking-tight leading-snug">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{description}</p>
            <div className="mt-3 flex items-start gap-2 bg-[#F5F3FF] border border-[#DDD6FE] rounded-lg p-3 text-xs">
              <Info className="w-4 h-4 mt-0.5 text-[#7C3AED] shrink-0" />
              <span className="text-zinc-700">
                <strong>Tip:</strong> {tip}
              </span>
            </div>
            <div className="mt-4">
              <Button onClick={onPrint} className="btn-primary rounded-full" data-testid={printDataTestId}>
                <Printer className="w-4 h-4 mr-2" /> {printLabel}
              </Button>
            </div>
          </div>
        </div>

        {/* Right: mini preview (clickable → modal) */}
        <button
          type="button"
          onClick={onPreviewClick}
          className="group relative rounded-xl border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow shrink-0 print:hidden mx-auto md:mx-0 justify-self-center md:justify-self-end"
          style={{ width: 180, height: 233 }}
          data-testid={previewDataTestId}
          title="Ver cartel completo"
        >
          <div className="origin-top-left absolute top-0 left-0" style={{ transform: "scale(0.22)", width: "8.5in", height: "11in" }}>
            <Poster {...posterProps} data-testid={testId} />
          </div>
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg">
              <Expand className="w-5 h-5 text-[#7C3AED]" />
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * ScaledPoster: scales the 8.5in×11in poster to fit container width
 * (used inside the preview modal so it works on mobile / narrow screens)
 * ------------------------------------------------------------------------- */
const POSTER_W = 816; // 8.5in @ 96dpi
const POSTER_H = 1056; // 11in @ 96dpi

function ScaledPoster(props) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / POSTER_W));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      className="mx-auto"
      style={{ width: "100%", maxWidth: POSTER_W, aspectRatio: "8.5 / 11", position: "relative", overflow: "hidden" }}
    >
      <div style={{ position: "absolute", top: 0, left: 0, width: POSTER_W, height: POSTER_H, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        <Poster {...props} />
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Poster visual (uses .poster classes from index.css)
 * ------------------------------------------------------------------------- */
function Poster({
  overline,
  vecino,
  headline,
  sub,
  scan,
  qrUrl,
  qrUrlLabel,
  footerLeftLabel,
  footerLeftName,
  footerLeftHint,
  footerRight,
  "data-testid": testId,
}) {
  return (
    <div className="poster mx-auto bg-white rounded-2xl shadow-lg print:shadow-none print:rounded-none" data-testid={testId}>
      <div className="poster-top">
        <div className="poster-overline">{overline}</div>
        <div className="poster-vecino">{vecino}</div>
      </div>

      <div className="poster-main">
        <div className="poster-headline">{headline}</div>
        <div className="poster-sub">{sub}</div>
      </div>

      <div className="poster-qr-block">
        <div className="poster-scan">{scan}</div>
        <div className="poster-qr-frame">
          <QRCodeSVG value={qrUrl} size={340} level="H" includeMargin={false} />
        </div>
        <div className="poster-url">{qrUrlLabel}</div>
      </div>

      <div className="poster-footer">
        <div className="poster-footer-left">
          <div className="poster-dir-label">{footerLeftLabel}</div>
          <div className="poster-dir-name" style={{ fontSize: "1.6rem" }}>{footerLeftName}</div>
          <div className="poster-dir-cp" style={{ marginTop: "0.3rem" }}>{footerLeftHint}</div>
        </div>
        <div className="poster-footer-right">{footerRight}</div>
      </div>
    </div>
  );
}